import { FeatureGate, type FeatureGateName } from '@orn/global/featureGates/featureGates.js';

const REGISTERED_GATE_NAMES: ReadonlySet<string> = new Set(Object.values(FeatureGate));

/** True when the gate name exists in the `FeatureGate` registry, i.e. some code path can reference it. */
export function isGateReferencedInCode(name: string): boolean {
  return REGISTERED_GATE_NAMES.has(name);
}

/**
 * Splits gate rows into `active` (name registered in `FeatureGate`, so code
 * can evaluate it) and `inactive` (a DB row no code references — either a
 * retired gate whose row was never deleted, or a name typo'd at creation).
 * Relative order within each group is preserved.
 */
export function partitionGatesByCodeUsage<T extends { name: string }>(
  gates: readonly T[]
): { active: T[]; inactive: T[] } {
  const active: T[] = [];
  const inactive: T[] = [];
  for (const gate of gates) {
    (isGateReferencedInCode(gate.name) ? active : inactive).push(gate);
  }
  return { active, inactive };
}

/**
 * Registry gate names that have no matching `feature_gates` row — the reverse
 * of the `inactive` set. Surfacing these lets an operator create the missing
 * row before the feature silently fails closed in staging/prod. Registry order
 * is preserved.
 */
export function findUnconfiguredGates(dbRows: readonly { name: string }[]): FeatureGateName[] {
  const dbNames = new Set(dbRows.map((row) => row.name));
  return (Object.values(FeatureGate) as FeatureGateName[]).filter((name) => !dbNames.has(name));
}
